import React, { useState } from "react";
import {
    Box,
    Button,
    Typography,
    IconButton,
    Chip,
    LinearProgress,
    Drawer,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Tooltip,
    useTheme,
    alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import SummarizeIcon from "@mui/icons-material/Summarize";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { useNavigate } from "react-router-dom";


export default function ResourceViewer({
    title,
    description,
    type,
    file,
    totalPages = 18,
    currentPage = 1,
    summary = "Learn how to use the LIMS platform and explore its crop monitoring features.",
    steps = [
        "Login to LIMS Monitoring Tool.",
        "Mark farm after geo locating.",
        "Enter farm name.",
    ],
}) {
    const navigate = useNavigate();
    const theme = useTheme();
    const isPDF = type === "pdf";
    const [isSummaryOpen, setIsSummaryOpen] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [page, setPage] = useState(currentPage);
    const progress = (page / totalPages) * 100;

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const toggleSummary = () => {
        setIsSummaryOpen(!isSummaryOpen);
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
        if (!isFullscreen) {
            document.documentElement.requestFullscreen?.().catch(() => {});
        } else {
            document.exitFullscreen?.().catch(() => {});
        }
    };

    return (
        <Box
            sx={{
                height: "100vh",
                width: "100vw",
                display: "flex",
                flexDirection: "column",
                bgcolor: "#0a0a0a",
                overflow: "hidden",
                position: "relative",
                padding: { xs: "8px", md: "16px" }, // outer spacing
                boxSizing: "border-box",
            }}
        >
            {/* Top Navigation Bar (overlay) */}
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    background: "linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)",
                    px: { xs: 2, md: 4 },
                    py: 3, // more vertical space
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                    pointerEvents: "none",
                    borderRadius: "12px 12px 0 0", // match padding rounding
                }}
            >
                {/* Left: Back + Title */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pointerEvents: "auto" }}>
                    <IconButton
                        onClick={() => navigate(-1)}
                        sx={{
                            color: "#fff",
                            bgcolor: alpha(theme.palette.common.white, 0.15),
                            backdropFilter: "blur(8px)",
                            "&:hover": {
                                bgcolor: alpha(theme.palette.common.white, 0.25),
                            },
                            width: 44,
                            height: 44,
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                color: "#fff",
                                textShadow: "0 2px 8px rgba(0,0,0,0.6)",
                                letterSpacing: "-0.02em",
                                fontSize: { xs: "1.1rem", md: "1.5rem" },
                            }}
                        >
                            {title}
                        </Typography>
                        {description && (
                            <Typography
                                sx={{
                                    color: alpha(theme.palette.common.white, 0.8),
                                    fontSize: "0.85rem",
                                    textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                                    maxWidth: "500px",
                                    display: { xs: "none", sm: "block" },
                                }}
                            >
                                {description}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Right: Actions */}
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", pointerEvents: "auto" }}>
                    <Tooltip title="View summary and key steps">
                    
                    </Tooltip>

                    <Button
                        component="a"
                        href={file}
                        download
                        startIcon={<DownloadIcon />}
                        variant="contained"
                        sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                            background: "linear-gradient(135deg, #2d8a4a, #1a6a32)",
                            boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
                            "&:hover": {
                                background: "linear-gradient(135deg, #237a3d, #145a28)",
                                boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
                            },
                        }}
                    >
                        {isPDF ? "Download PDF" : "Download Video"}
                    </Button>
                </Box>
            </Box>

            {/* Main Viewer (full area with inner padding) */}
            <Box
                sx={{
                    flex: 1,
                    position: "relative",
                    bgcolor: "#000",
                    borderRadius: "12px",
                    overflow: "hidden",
                    mt: "64px", // offset for the absolute header so content starts below it
                    mx: { xs: 0, md: 0 }, // no side margin because we already have padding on outer container
                    // inner spacing around the iframe/video
                    p: { xs: 1, md: 2 },
                    boxSizing: "border-box",
                }}
            >
                {isPDF ? (
                    <>
                        <Box
                            sx={{
                                position: "relative",
                                width: "100%",
                                height: "100%",
                                bgcolor: "#fafcfa",
                                borderRadius: "8px",
                                overflow: "hidden",
                            }}
                        >
                            <iframe
                                src={`${file}#page=${page}`}
                                title={title}
                                width="100%"
                                height="100%"
                                style={{
                                    border: "none",
                                    background: "#fff",
                                }}
                            />
                        </Box>
                        {/* PDF Controls Overlay (bottom) */}
                        <Box
                            sx={{
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                zIndex: 10,
                                background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
                                px: { xs: 2, md: 4 },
                                py: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 2,
                                pointerEvents: "none",
                                borderRadius: "0 0 8px 8px",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, pointerEvents: "auto" }}>
                                <Chip
                                    icon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
                                    label="PDF"
                                    size="small"
                                    sx={{
                                        bgcolor: alpha(theme.palette.common.white, 0.15),
                                        color: "#fff",
                                        backdropFilter: "blur(4px)",
                                        fontWeight: 600,
                                        borderRadius: "6px",
                                        height: 28,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#fff",
                                        fontWeight: 500,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                                    }}
                                >
                                    <MenuBookIcon sx={{ fontSize: 18 }} />
                                    Page {page} of {totalPages}
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 3, pointerEvents: "auto", flex: 1, maxWidth: 300 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{
                                        flex: 1,
                                        height: 4,
                                        borderRadius: 2,
                                        bgcolor: alpha(theme.palette.common.white, 0.2),
                                        "& .MuiLinearProgress-bar": {
                                            borderRadius: 2,
                                            background: "#2d8a4a",
                                        },
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: "#fff",
                                        minWidth: 44,
                                        textAlign: "right",
                                        textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                                    }}
                                >
                                    {Math.round(progress)}%
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pointerEvents: "auto" }}>
                                <IconButton
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page <= 1}
                                    sx={{
                                        color: "#fff",
                                        "&:hover": {
                                            bgcolor: alpha(theme.palette.common.white, 0.15),
                                        },
                                        "&.Mui-disabled": {
                                            color: alpha(theme.palette.common.white, 0.3),
                                        },
                                    }}
                                >
                                    <NavigateBeforeIcon />
                                </IconButton>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#fff",
                                        fontWeight: 500,
                                        minWidth: 60,
                                        textAlign: "center",
                                        textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                                    }}
                                >
                                    {page} / {totalPages}
                                </Typography>
                                <IconButton
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page >= totalPages}
                                    sx={{
                                        color: "#fff",
                                        "&:hover": {
                                            bgcolor: alpha(theme.palette.common.white, 0.15),
                                        },
                                        "&.Mui-disabled": {
                                            color: alpha(theme.palette.common.white, 0.3),
                                        },
                                    }}
                                >
                                    <NavigateNextIcon />
                                </IconButton>
                                <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                                    <IconButton
                                        onClick={toggleFullscreen}
                                        sx={{
                                            color: "#fff",
                                            "&:hover": {
                                                bgcolor: alpha(theme.palette.common.white, 0.15),
                                            },
                                        }}
                                    >
                                        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </>
                ) : (
                    // Video
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            bgcolor: "#000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "8px",
                            overflow: "hidden",
                        }}
                    >
                        <video
                            controls
                            autoPlay={false}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                background: "#000",
                            }}
                        >
                            <source src={file} type="video/mp4" />
                            Your browser does not support video playback.
                        </video>
                    </Box>
                )}
            </Box>

            {/* Summary Drawer */}
            <Drawer
                anchor="right"
                open={isSummaryOpen}
                onClose={toggleSummary}
                PaperProps={{
                    sx: {
                        width: { xs: "100%", sm: 400 },
                        borderRadius: { xs: 0, sm: "20px 0 0 20px" },
                        p: 3,
                        bgcolor: "#f8fbf8",
                        borderLeft: "1px solid #e4ede4",
                    },
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: "#1a2e24",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <SummarizeIcon sx={{ color: "#2d8a4a" }} />
                        Summary
                    </Typography>
                    <IconButton onClick={toggleSummary} sx={{ color: "#5a6b5f" }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Typography
                    variant="body1"
                    sx={{
                        color: "#2d4a3b",
                        lineHeight: 1.7,
                        mb: 3,
                        fontSize: "0.95rem",
                    }}
                >
                    {summary}
                </Typography>

                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 600,
                        color: "#1a2e24",
                        mb: 2,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.75rem",
                    }}
                >
                    Quick Overview
                </Typography>

                <List sx={{ p: 0 }}>
                    {steps.map((step, index) => (
                        <ListItem
                            key={index}
                            sx={{
                                px: 2,
                                py: 1.5,
                                mb: 1,
                                bgcolor: "#fff",
                                borderRadius: "12px",
                                border: "1px solid #e8efe8",
                                transition: "all 0.2s",
                                "&:hover": {
                                    borderColor: "#b8d0b8",
                                    bgcolor: "#fafffa",
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: "50%",
                                        bgcolor: "#e8f2e8",
                                        color: "#1a5a2a",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "0.75rem",
                                        fontWeight: 700,
                                    }}
                                >
                                    {index + 1}
                                </Box>
                            </ListItemIcon>
                            <ListItemText
                                primary={step}
                                primaryTypographyProps={{
                                    fontSize: "0.9rem",
                                    color: "#2d4a3b",
                                    fontWeight: 500,
                                }}
                            />
                        </ListItem>
                    ))}
                </List>

                <Box sx={{ mt: "auto", pt: 3 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<CheckCircleIcon />}
                        sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            py: 1.5,
                            borderColor: "#c5d5c5",
                            color: "#1a3a2a",
                            "&:hover": {
                                borderColor: "#8aaa8a",
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                            },
                        }}
                    >
                        Mark as Complete
                    </Button>
                </Box>
            </Drawer>
        </Box>
    );
}